import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ConsensoMarketing } from "../components/ConsensoMarketing";
import { useBrandFont } from "../lib/brandFonts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const FIELD_INPUT_TYPE = { email: "email", data_nascita: "date" };

function fmtCountdown(ms) {
  const s = Math.floor(ms / 1000);
  const pad = (n) => String(n).padStart(2, "0");
  const dd = Math.floor(s / 86400);
  return (dd > 0 ? `${dd}g ` : "")
    + [Math.floor((s % 86400) / 3600), Math.floor((s % 3600) / 60), s % 60].map(pad).join(":");
}

export default function CouponPubblico() {
  const { rid, slug } = useParams();
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState(null);
  const [fields, setFields] = useState({});
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [formError, setFormError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState("landing"); // landing personalizzata → form dati
  const [showCond, setShowCond] = useState(false); // overlay condizioni di utilizzo
  const [selectedOffer, setSelectedOffer] = useState(null); // vetrina: offerta scelta
  const [goBooking, setGoBooking] = useState(false); // vetrina booking-first: redirect al modulo
  const refCode = new URLSearchParams(window.location.search).get("ref") || "";

  useEffect(() => {
    axios.get(`${API}/public/coupon/${rid}/${slug}`)
      .then((r) => setCfg(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Coupon non disponibile"));
  }, [rid, slug]);

  useBrandFont(cfg?.brand?.font || "");

  const [cdLeft, setCdLeft] = useState(0);
  const cdTarget = cfg?.coupon?.countdown_target;
  useEffect(() => {
    if (!cdTarget) return;
    const tick = () => setCdLeft(Math.max(0, new Date(cdTarget).getTime() - Date.now()));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [cdTarget]);

  // Pixel Meta: lo script viene caricato dalla landing stessa se configurato
  const pixelId = cfg?.coupon?.tracciamenti?.fb_pixel_id;
  useEffect(() => {
    if (!pixelId || window.fbq) return;
    const n = window.fbq = function (...a) { n.callMethod ? n.callMethod(...a) : n.queue.push(a); };
    n.queue = []; n.loaded = true; n.version = "2.0";
    const s = document.createElement("script");
    s.async = true; s.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(s);
    window.fbq("init", pixelId);
    window.fbq("track", "PageView");
  }, [pixelId]);

  // Tracciamenti conversione + redirect opzionale verso il modulo di prenotazione
  useEffect(() => {
    if (!done) return;
    const tr = cfg?.coupon?.tracciamenti || {};
    try {
      if (tr.ga4_event && window.gtag) window.gtag("event", tr.ga4_event);
      if (window.fbq) {
        window.fbq("track", "Lead");
        if (tr.fb_event) window.fbq("trackCustom", tr.fb_event);
      }
    } catch { /* tracciamento best-effort */ }
    const ty = done.thankyou || {};
    if (ty.modalita === "redirect") {
      const url = ty.redirect_url || done.web_form_url;
      if (url) {
        const t = setTimeout(() => { window.location.assign(url); }, 2000);
        return () => clearTimeout(t);
      }
    }
  }, [done]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4" data-testid="cp-errore">
        <div className="bg-white rounded-xl p-6 text-center max-w-sm w-full">
          <div className="font-display font-bold text-zinc-900">Coupon non disponibile</div>
          <p className="text-sm text-zinc-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }
  if (!cfg) return <div className="min-h-screen bg-zinc-100 flex items-center justify-center text-sm text-zinc-400">Caricamento...</div>;

  const { coupon, brand } = cfg;
  const vetrinaBookingFirst = coupon.tipo === "vetrina" && coupon.vetrina_booking_first && coupon.web_form_url;
  const bookingUrl = (offer) => {
    const base = coupon.web_form_url || "";
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}offer=${offer?.coupon_id || ""}&offer_title=${encodeURIComponent(offer?.titolo || "")}`;
  };
  const primary = brand?.colors?.primary || "#01B2E0";
  const lnd = coupon.landing || {};
  const dimTitoloCls = { grande: "text-2xl", media: "text-xl", piccola: "text-lg" }[lnd.dim_titolo || "grande"];

  const submit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSending(true);
    try {
      const r = await axios.post(`${API}/public/coupon/${rid}/${slug}/download`, { fields, consent, website, ref: refCode, offer_coupon_id: selectedOffer?.coupon_id || null });
      setDone(r.data);
    } catch (err) {
      setFormError(err.response?.data?.detail || "Invio non riuscito, riprova");
    } finally { setSending(false); }
  };

  if (done) {
    const ty = done.thankyou || {};
    return (
      <div className="min-h-screen bg-zinc-100 flex items-start justify-center p-4 pt-10" data-testid="cp-thankyou" data-brand-font="">
        <div className="bg-white rounded-xl p-6 max-w-md w-full text-center shadow-sm">
          {brand?.assets?.logo && <img src={brand.assets.logo} alt={brand.name} className="h-16 mx-auto mb-4 object-contain" />}
          <h1 className="font-display text-xl font-bold text-zinc-900" data-testid="cp-ty-titolo">
            {ty.titolo || "Coupon scaricato con successo!"}
          </h1>
          {ty.descrizione && (
            <div className="text-sm text-zinc-600 mt-3 text-left [&_ul]:list-disc [&_ul]:pl-5 [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: ty.descrizione }} data-testid="cp-ty-descrizione" />
          )}
          {ty.modalita === "redirect" && (ty.redirect_url || done.web_form_url) && (
            <div className="mt-4 text-sm text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2" data-testid="cp-redirect">
              Ti sto portando alla pagina di prenotazione...{" "}
              <a href={ty.redirect_url || done.web_form_url} className="underline font-semibold"
                data-testid="cp-redirect-link">Clicca qui se non vieni reindirizzato</a>
            </div>
          )}
          <div className="mt-4 rounded-lg border-2 border-dashed p-4" style={{ borderColor: primary }} data-testid="cp-code-box">
            <div className="text-xs font-semibold text-zinc-500 uppercase">Il tuo codice coupon</div>
            <div className="font-mono font-bold text-2xl text-zinc-900" data-testid="cp-code">{done.code}</div>
            <div className="text-[11px] text-zinc-400 mt-1">Mostralo in cassa oppure presentalo alla prenotazione</div>
          </div>
          {done.notify?.channel === "whatsapp" && done.notify?.status === "inviato" && (
            <p className="text-xs text-emerald-700 mt-3" data-testid="cp-notify-wa">Ti abbiamo inviato la conferma su WhatsApp.</p>
          )}
          {done.notify?.channel === "email" && done.notify?.status === "inviato" && (
            <p className="text-xs text-emerald-700 mt-3" data-testid="cp-notify-email">Ti abbiamo inviato la conferma via email.</p>
          )}
          {done.web_form_url && (
            <a href={done.web_form_url} data-testid="cp-prenota"
              className="mt-4 inline-block h-12 px-6 rounded-md text-white text-sm font-semibold leading-[3rem] active:scale-95 transition"
              style={{ backgroundColor: primary }}>
              Prenota ora il tuo tavolo
            </a>
          )}
          <div className="flex gap-2 justify-center mt-3 flex-wrap">
            {cfg.coupon.pdf?.attivo && (
              <a href={`${API}/public/coupon-pdf/${rid}/${done.code}`} target="_blank" rel="noreferrer" data-testid="cp-pdf"
                className="h-10 px-4 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 leading-10">
                Scarica coupon PDF
              </a>
            )}
          </div>
          {done.share_url && cfg.coupon.referral?.attivo && (
            <div className="mt-4 bg-zinc-50 border border-zinc-200 rounded-lg p-3" data-testid="cp-share">
              <div className="text-xs font-semibold text-zinc-700">Condividi con un amico</div>
              <p className="text-[11px] text-zinc-400 mt-0.5">Con il tuo link personale tracciamo chi ci porta nuovi clienti</p>
              <div className="flex gap-2 mt-2 justify-center">
                <a href={`https://wa.me/?text=${encodeURIComponent((cfg.coupon.referral?.messaggio || "Ho trovato questo coupon, approfittane!") + " " + done.share_url)}`}
                  target="_blank" rel="noreferrer" data-testid="cp-share-wa"
                  className="h-10 px-4 rounded-md bg-emerald-600 text-white text-xs font-semibold leading-10">
                  WhatsApp
                </a>
                <button onClick={async () => {
                  try { await navigator.clipboard.writeText(done.share_url); setCopied(true); }
                  catch { window.prompt("Copia manualmente il link:", done.share_url); }
                }}
                  data-testid="cp-share-copy"
                  className="h-10 px-4 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700">
                  {copied ? "Copiato!" : "Copia link"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const allineaCentro = lnd.allinea_testo === "centro";

  return (
    <div className="min-h-screen bg-zinc-100 sm:py-6 sm:px-4" data-testid="coupon-pubblico" data-brand-font="">
      {coupon.landing?.immagine && (
        <img src={coupon.landing.immagine} alt={coupon.landing?.titolo || coupon.name}
          className={`w-full h-[44vh] min-h-64 max-h-[30rem] sm:rounded-t-xl ${coupon.landing?.img_fit === "copri" ? "object-cover" : "object-contain bg-zinc-900"}`}
          data-testid="cp-immagine" />
      )}
      <div className={`max-w-md mx-auto bg-white shadow-sm relative z-10 ${coupon.landing?.immagine ? "-mt-10 rounded-t-3xl sm:rounded-xl overflow-hidden" : "rounded-xl overflow-hidden my-6 sm:my-0 mx-4 sm:mx-auto"}`}>
        <div className="p-5 border-b border-zinc-100 text-center" style={{ backgroundColor: lnd.sfondo_header || `${primary}14` }}>
          {brand?.assets?.logo && <img src={brand.assets.logo} alt={brand.name} className="h-14 mx-auto mb-2 object-contain" />}
          <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">{brand?.name}</div>
          <h1 className={`font-display ${dimTitoloCls} font-bold mt-1`}
            style={{ color: lnd.colore_titolo || "#18181b" }} data-testid="cp-titolo">
            {lnd.titolo || coupon.name}
          </h1>
          {lnd.sottotitolo && (
            <p className="text-sm mt-1" style={{ color: lnd.colore_sottotitolo || "#71717a" }}
              data-testid="cp-sottotitolo">{lnd.sottotitolo}</p>
          )}
        </div>

        {step === "landing" ? (
          <div className="p-5 space-y-5" data-testid="cp-landing">
            {lnd.descrizione && (
              <div className={`text-sm [&_ul]:list-disc [&_ul]:pl-5 [&_a]:underline ${allineaCentro ? "text-center [&_ul]:text-left" : ""}`}
                style={{ color: lnd.colore_testo || "#3f3f46" }}
                dangerouslySetInnerHTML={{ __html: lnd.descrizione }} data-testid="cp-descrizione" />
            )}
            {coupon.landing?.video_youtube && (
              <div className="rounded-lg overflow-hidden border border-zinc-200" data-testid="cp-video">
                <iframe title="Video promozionale" className="w-full aspect-video" allowFullScreen
                  src={`https://www.youtube-nocookie.com/embed/${coupon.landing.video_youtube}`} />
              </div>
            )}
            {coupon.valid_to && (
              <p className="text-xs text-zinc-500" data-testid="cp-validita">
                Valido dal {coupon.valid_from} al {coupon.valid_to}
              </p>
            )}
            {coupon.landing?.condizioni && (
              <button type="button" onClick={() => setShowCond(true)} data-testid="cp-condizioni-btn"
                className="w-full text-center text-xs font-semibold underline underline-offset-2 text-zinc-500 hover:text-zinc-800 transition-colors">
                Condizioni di utilizzo
              </button>
            )}
            {showCond && coupon.landing?.condizioni && (
              <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="cp-condizioni-overlay" role="dialog" aria-modal="true">
                <div className="flex items-center justify-between px-4 h-14 border-b border-zinc-100 shrink-0">
                  <span className="text-sm font-bold text-zinc-900">Condizioni di utilizzo</span>
                  <button type="button" onClick={() => setShowCond(false)} data-testid="cp-condizioni-close"
                    aria-label="Chiudi condizioni"
                    className="h-9 w-9 rounded-full bg-zinc-100 text-zinc-600 text-base leading-none active:scale-95 transition-transform">✕</button>
                </div>
                <div className={`flex-1 overflow-y-auto p-5 text-sm text-zinc-600 [&_ul]:list-disc [&_ul]:pl-5 [&_a]:underline ${allineaCentro ? "text-center [&_ul]:text-left" : ""}`}
                  dangerouslySetInnerHTML={{ __html: coupon.landing.condizioni }} data-testid="cp-condizioni-testo" />
                <div className="p-4 border-t border-zinc-100 shrink-0">
                  <button type="button" onClick={() => setShowCond(false)} data-testid="cp-condizioni-ok"
                    className="w-full h-12 rounded-md text-white font-semibold text-sm active:scale-[0.98] transition"
                    style={{ backgroundColor: primary }}>
                    Ho capito
                  </button>
                </div>
              </div>
            )}
            {cdTarget && cdLeft > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-center" data-testid="cp-countdown">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">L'offerta scade tra</div>
                <div className="font-display text-2xl font-extrabold text-zinc-900 mt-0.5" data-testid="cp-countdown-value">
                  {fmtCountdown(cdLeft)}
                </div>
              </div>
            )}
            {coupon.landing?.mostra_disponibilita && cfg.coupon.rimanenti != null && (
              <p className="text-xs font-bold text-rose-600 text-center" data-testid="cp-rimanenti">
                {cfg.coupon.rimanenti > 0 ? `Solo ${cfg.coupon.rimanenti} coupon rimasti!` : "Coupon esauriti"}
              </p>
            )}
            {cfg.coupon.scaduto ? (
              <div className="rounded-lg bg-zinc-100 border border-zinc-200 p-4 text-center text-sm font-semibold text-zinc-500"
                data-testid="cp-scaduto">
                Questa offerta è scaduta. Seguici per le prossime promozioni!
              </div>
            ) : coupon.tipo === "vetrina" && !selectedOffer ? (
              <div className="space-y-3" data-testid="cp-offerte">
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 text-center">
                  Scegli la tua offerta
                </div>
                {(coupon.offers || []).map((o, i) => (
                  <button key={o.coupon_id} type="button" onClick={() => setSelectedOffer(o)}
                    data-testid={`cp-offerta-${i}`}
                    className="w-full rounded-xl border-2 border-zinc-200 bg-white text-left overflow-hidden active:scale-[0.98] transition hover:border-zinc-400">
                    {o.immagine && (
                      <img src={o.immagine} alt={o.titolo} className="w-full h-28 object-cover" />
                    )}
                    <div className="p-4">
                      <div className="font-display font-bold text-base text-zinc-900" data-testid={`cp-offerta-titolo-${i}`}>
                        {o.titolo}
                      </div>
                      {o.sottotitolo && (
                        <div className="text-xs text-zinc-500 mt-0.5">{o.sottotitolo}</div>
                      )}
                      {o.descrizione && (
                        <div className="text-sm text-zinc-600 mt-2 [&_ul]:list-disc [&_ul]:pl-5"
                          dangerouslySetInnerHTML={{ __html: o.descrizione }} />
                      )}
                      <div className="mt-3 h-10 rounded-md text-white text-sm font-semibold flex items-center justify-center"
                        style={{ backgroundColor: primary }}>
                        {vetrinaBookingFirst ? "Prenota con questa offerta" : "Scegli questa offerta"}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : vetrinaBookingFirst && selectedOffer ? (
              <div className="space-y-3" data-testid="cp-booking-step">
                <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 flex items-center justify-between gap-3" data-testid="cp-offerta-scelta">
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Offerta scelta</div>
                    <div className="font-display font-bold text-sm text-zinc-900 truncate" data-testid="cp-offerta-scelta-titolo">
                      {selectedOffer.titolo}
                    </div>
                  </div>
                  <button type="button" onClick={() => setSelectedOffer(null)} data-testid="cp-cambia-offerta"
                    className="text-xs font-semibold underline text-zinc-500 hover:text-zinc-900 shrink-0">
                    Cambia
                  </button>
                </div>
                <p className="text-xs text-zinc-500 text-center" data-testid="cp-booking-hint">
                  Prenota il tuo tavolo: il coupon ti verrà assegnato automaticamente in base al numero di persone.
                </p>
                <a href={bookingUrl(selectedOffer)} data-testid="cp-go-booking"
                  className="w-full h-12 rounded-md text-white font-semibold text-sm flex items-center justify-center active:scale-[0.98] transition"
                  style={{ backgroundColor: primary }}>
                  Prenota ora il tuo tavolo
                </a>
              </div>
            ) : (
              <div className="space-y-3">
                {coupon.tipo === "vetrina" && selectedOffer && (
                  <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 flex items-center justify-between gap-3" data-testid="cp-offerta-scelta">
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Offerta scelta</div>
                      <div className="font-display font-bold text-sm text-zinc-900 truncate" data-testid="cp-offerta-scelta-titolo">
                        {selectedOffer.titolo}
                      </div>
                    </div>
                    <button type="button" onClick={() => setSelectedOffer(null)} data-testid="cp-cambia-offerta"
                      className="text-xs font-semibold underline text-zinc-500 hover:text-zinc-900 shrink-0">
                      Cambia
                    </button>
                  </div>
                )}
                <button onClick={() => setStep("form")} data-testid="cp-open-form"
                  className="w-full h-12 rounded-md text-white font-semibold text-sm active:scale-[0.98] transition"
                  style={{ backgroundColor: primary }}>
                  {coupon.tipo === "vetrina" ? "Scarica il coupon" : (coupon.landing?.cta_label || "Scarica il coupon")}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="p-5 space-y-4" data-testid="cp-form-step">
            <button onClick={() => setStep("landing")} data-testid="cp-back"
              className="text-xs font-semibold text-zinc-500 hover:text-zinc-900">
              ← Torna alla promozione
            </button>
            {coupon.tipo === "vetrina" && selectedOffer && (
              <div className="rounded-lg border-2 p-3" style={{ borderColor: primary }} data-testid="cp-form-offerta">
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Stai scaricando</div>
                <div className="font-display font-bold text-sm text-zinc-900">{selectedOffer.titolo}</div>
              </div>
            )}
            <form onSubmit={submit} className="space-y-4">
              <input value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off"
                className="hidden" aria-hidden="true" />
              <div className="grid grid-cols-2 gap-2">
                {coupon.fields.map((f) => (
                  <div key={f.key} className={["nome", "cognome"].includes(f.key) ? "" : "col-span-2"}>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">
                      {f.label}{f.required ? " *" : ""}
                    </label>
                    <input type={FIELD_INPUT_TYPE[f.key] || "text"} value={fields[f.key] || ""} required={f.required}
                      onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })} data-testid={`cp-field-${f.key}`}
                      className="w-full h-11 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2" />
                  </div>
                ))}
              </div>
              <ConsensoMarketing rid={rid} checked={consent} onChange={setConsent}
                testid="cp-consenso" accentColor={primary} />
              {formError && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="cp-error">{formError}</div>}
              <button type="submit" disabled={sending} data-testid="cp-submit"
                className="w-full h-12 rounded-md text-white font-semibold text-sm active:scale-[0.98] transition disabled:opacity-50"
                style={{ backgroundColor: primary }}>
                {sending ? "Invio in corso..." : coupon.landing?.cta_label || "Scarica il coupon"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
